/* eslint-disable no-param-reassign */
import React, {
    useContext, useCallback, useEffect,
    useState
} from 'react';
import _ from 'lodash';
import { TextInput, useModal } from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { ViewModelServiceContext, ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { EntityUtil } from '@xengage/gw-portals-util-js';
import { useUpdateSubmissionServices, useCheckSubmissionVMDirty, screenReaderAnnounce, focusNextElement, validationUtil } from 'premier-capability-hooks';
import { PremierConstants } from 'premier-constant-service';
import presentationMetadata from './RiskAnalysisPage.metadata.json5';
import styles from './RiskAnalysisPage.module.scss'
import messages from './RiskAnalysisPage.messages';

function RiskAnalysisPage(props) {
    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const { showConfirm } = useModal();
    const {
        wizardData, updateWizardData
    } = props;
    const jobNumber = wizardData.value?.quoteID || wizardData.value?.jobID;
    const viewModelService = useContext(ViewModelServiceContext);
    const { saveAndQuoteSubmissionService } = useUpdateSubmissionServices();
    const { isDirty } = useCheckSubmissionVMDirty(wizardData.value);
    const {
        isComponentValid,
        initialValidation,
        onValidate,
    } = useValidation('RiskAnalysisPage');

    const metadata = presentationMetadata.riskAnalysis;
    const { lineName } = PremierConstants;
    const { updateSubmission } = useUpdateSubmissionServices();
    const [notifications, setNotifications] = useState([]);

    const riskAnalysis = _.get(
        wizardData,
        'baseData.priorLossHistoryRecords.value'
    );

    const priorPolicies = _.get(
        wizardData,
        'baseData.priorPolicies_Ext.value'
    );


    useEffect(() => {
        wizardData.baseData?.priorLossHistoryRecords?.children.forEach((item) => {
            item.isValidationRequired.value = false;
        });
    }, [wizardData]);


    const addriskAnalysis = useCallback(() => {
        const rvriskAnalysiss = _.get(
            wizardData,
            'baseData.priorLossHistoryRecords'
        );

        rvriskAnalysiss.value = rvriskAnalysiss.value || [];

        const { _xCenter, _dtoName } = rvriskAnalysiss;
        const riskAnalysisVM = viewModelService.create({
            productLine: wizardData.baseData.productName.value,
            tempID: EntityUtil.nextId(),
        }, _xCenter, _dtoName);

        rvriskAnalysiss.pushElement(riskAnalysisVM);

        const newwizardData = viewModelService.clone(wizardData);

        _.set(wizardData, 'wizardData', newwizardData);
        updateWizardData(wizardData);
        screenReaderAnnounce('Claim fields have been populated');
        focusNextElement(true, 2);
    }, [wizardData, updateWizardData, viewModelService]);

    const addPriorPolicy = useCallback(() => {
        const priorPoliciesL = _.get(
            wizardData,
            'baseData.priorPolicies_Ext'
        );

        priorPoliciesL.value = priorPoliciesL.value || [];

        const { _xCenter, _dtoName } = priorPoliciesL;
        const priorPolicyVM = viewModelService.create({
            tempID: EntityUtil.nextId(),
        }, _xCenter, _dtoName);

        priorPoliciesL.pushElement(priorPolicyVM);

        const newwizardData = viewModelService.clone(wizardData);

        _.set(wizardData, 'wizardData', newwizardData);
        updateWizardData(wizardData);
        screenReaderAnnounce('Fields for new claim');
        focusNextElement(true, 2);
    }, [wizardData, updateWizardData, viewModelService]);

    const onModelChange = (updatedVM) => {
        const newwizardData = viewModelService.clone(updatedVM);

        _.set(wizardData, 'wizardData', newwizardData);
        updateWizardData(wizardData);
    };

    const removeriskAnalysis = useCallback(
        (evt) => {

            const riskAnalysisListPath = 'baseData.priorLossHistoryRecords.value';
            const riskAnalysisPath = evt.path;
            // eslint-disable-next-line @typescript-eslint/no-shadow
            const riskAnalysis = _.get(wizardData, riskAnalysisPath).value;
            const riskAnalysisList = _.get(wizardData, riskAnalysisListPath);
            // const riskAnalysisIndex = riskAnalysisList.findIndex(
            //     (riskAnalysisFromList) => _.isEqual(riskAnalysisFromList, riskAnalysis)
            // );

            showConfirm({
                title: messages.removeriskAnalysis,
                message: messages.removeriskAnalysisDesc,
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: messages.additionalModalYes,
                cancelButtonText: messages.additionalModalNo
            }).then(
                (results) => {
                    if (results === 'cancel') {
                        screenReaderAnnounce('Action cancelled');

                        return _.noop();
                    }

                    const newriskAnalysisList = _.remove(riskAnalysisList,
                        (riskAnalysisFromList) => _.isEqual(riskAnalysisFromList, riskAnalysis));

                    _.set(wizardData, newriskAnalysisList, riskAnalysisListPath);

                    const newwizardData = viewModelService.clone(wizardData);

                    _.set(wizardData, 'wizardData', newwizardData);
                    updateWizardData(wizardData);
                    
                    setTimeout(() => {
                        screenReaderAnnounce(`Claim ${evt.index + 1} deleted`);
                        focusNextElement(true, 0, 'rvAddriskAnalysisBtn');
                    }, 0);               

                    return true;
                }, _.noop
            );
        },
        [wizardData, showConfirm, viewModelService, updateWizardData]
    );

    const removePriorPolicy = useCallback(
        (evt) => {

            const priorPolicyListPath = 'baseData.priorPolicies_Ext.value';
            const priorPolicyPath = evt.path;
            const priorPolicy = _.get(wizardData, priorPolicyPath).value;
            const priorPolicyList = _.get(wizardData, priorPolicyListPath);

            showConfirm({
                title: messages.removeriskAnalysis,
                message: messages.removeriskAnalysisDesc,
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: messages.additionalModalYes,
                cancelButtonText: messages.additionalModalNo
            }).then(
                (results) => {
                    if (results === 'cancel') {
                        screenReaderAnnounce('Action cancelled');

                        return _.noop();
                    }

                    const newPriorPolicyList = _.remove(priorPolicyList,
                        (priorPolicyFromList) => _.isEqual(priorPolicyFromList, priorPolicy));

                    _.set(wizardData, newPriorPolicyList, priorPolicyListPath);

                    const newwizardData = viewModelService.clone(wizardData);

                    _.set(wizardData, 'wizardData', newwizardData);
                    updateWizardData(wizardData);
                    
                    setTimeout(() => {
                        screenReaderAnnounce('Prior policy deleted');
                        focusNextElement(true, 0, 'rvAddPriorPolicyBtn');
                    }, 0);

                    return true;
                }, _.noop
            );
        },
        [wizardData, showConfirm, viewModelService, updateWizardData]
    );


    const overrideTitleProps = useCallback(() => {
        if (riskAnalysis !== undefined) {
            const overrideProps = riskAnalysis.map((value, index) => {
                value.isValidationRequired = true;

                return {

                    [`rvriskAnalysisCount${index}`]: {
                        content: translator(messages.riskAnalysisDetailsCount, {
                            count: index + 1
                        })
                    },
                    [`incidentClaim${index}`]: {
                    },
                    [`lossDate${index}`]: {

                    },
                    [`rvDeleteriskAnalysis${index}`]: {
                        alert: translator(messages.riskAnalysisDelete, {
                            count: index + 1
                        })
                    },
                    [`amountPaid${index}`]: {
                    },
                    [`typeofLoss${index}`]: {
                    },
                    [`lossDate${index}`]: {
                    },
                    [`details${index}`]: {

                    },
                    [`repRes${index}`]: {
                    }
                };
            });

            return Object.assign({}, ...overrideProps);
        }

        return null;
    }, [riskAnalysis, translator]);

    const onNext = useCallback(async () => {

        const { jobType } = wizardData.value.baseData;

        const oldIssues = notifications;

        setNotifications([]);

        const quoteResponse = await saveAndQuoteSubmissionService('ProLine', 'RiskAnalysisPage', jobNumber, wizardData.value, jobType, isDirty);

        wizardData.value = quoteResponse.quoteResponse;

        if (_.isEqual(_.get(wizardData.value, 'baseData.periodStatus', ''), 'Quoted')) {
            _.set(wizardData, 'errorsAndWarnings.validationIssues.value', {});
            updateWizardData(wizardData);

            return wizardData;
        }

        const errorsAndWarnings = _.get(quoteResponse.quoteResponse, `errorsAndWarnings`, {});
        const notificationData = validationUtil.handleEdgeError(errorsAndWarnings, oldIssues);

        if (!notificationData.hasNewIssues && !notificationData.containsError) {
            _.set(wizardData, 'errorsAndWarnings.validationIssues.value', {});

            updateWizardData(wizardData);

            return wizardData;
        }

        setNotifications(notificationData.issuesList);
        window.scrollTo(0, 0);

        return false;

    }, [isDirty, jobNumber, notifications, saveAndQuoteSubmissionService, updateWizardData, wizardData]);

    const onSave = useCallback(
        async () => {
            const subVMCopy = _.clone(wizardData)
            const productCode = _.get(subVMCopy.value, 'baseData.productCode');

            if(subVMCopy.baseData.priorLossHistoryRecords.value.length > 0) {
                validationUtil.unSetIsValidationReqFlag(subVMCopy.baseData.priorLossHistoryRecords.value, ' ')
            }

            if(subVMCopy.baseData.priorPolicies_Ext.value.length > 0) {
                validationUtil.unSetIsValidationReqFlag(subVMCopy.baseData.priorPolicies_Ext.value, ' ')
            }            

            const lobLineName = productCode ? lineName[productCode] : '';

            const submissionData = await updateSubmission(lobLineName, 'RiskAnalysisPage', subVMCopy);
            wizardData.value = submissionData;
            updateWizardData(wizardData);
                            
            return wizardData;
        },
        [lineName, wizardData, updateSubmission]
    );

    const overridePriorPolicies = useCallback(() => {
        if (priorPolicies !== undefined) {
            const overrideProps = priorPolicies.map((value, index) => {
                value.isValidationRequired = true;

                return {

                    [`priorPolicyCount${index}`]: {
                        content: translator(messages.priorPolicyDetailsCount, {
                            count: index + 1
                        })
                    },
                    [`insNamOth${index}`]: {
                        readOnly: _.isUndefined(value.insuName) || value.insuName !== 'Other'
                    }
                };
            });

            return Object.assign({}, ...overrideProps);
        }

        return null;
    }, [priorPolicies, translator]);

    const getTotalAmountPaid = () => {
        let totalAmtPaid = 0;

        riskAnalysis.forEach(element => {
            if(!_.isUndefined(element.amountPaid)) {
                totalAmtPaid += element.amountPaid
            }
        })

        return totalAmtPaid
    };

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
            showRequired: true
        },
        totalIncidentsCount: {
            value: riskAnalysis.length > 0 ? riskAnalysis.length : '0'
        },
        totalAmountPaid: {
            value: getTotalAmountPaid() > 0 ? getTotalAmountPaid() : '0'
        },
        rvAddAnotherriskAnalysis: {
            visible: riskAnalysis !== undefined ? riskAnalysis.length > 0 : false
        },
        rvAddAnotherPriorPolicy: {
            visible: priorPolicies !== undefined ? priorPolicies.length > 0 : false
        },
        contentEmptyContainer: {
            visible: riskAnalysis !== undefined ? riskAnalysis.length === 0 : true
        },
        customNotification: {
            content: validationUtil.getNotificationsContent(notifications)
        },
        agmtSubTypInd_Ext: {
            visible: wizardData.value.baseData.priorLossHistoryRecords.length > 0,
            required: wizardData.value.baseData.priorLossHistoryRecords.length > 0
        },
        ...overrideTitleProps(),
        ...overridePriorPolicies()
    };

    const resolvers = {
        resolveComponentMap: {
            TextInput
        },
        resolveCallbackMap: {
            onAddriskAnalysis: addriskAnalysis,
            onAddPriorPolicy: addPriorPolicy,
            onRemoveriskAnalysis: removeriskAnalysis,
            onRemovePriorPolicy: removePriorPolicy
        },
        resolveClassNameMap: styles
    };

    const isDisableNext = () => {
        if(wizardData.value.baseData.priorLossHistoryRecords.length > 0) {
            return !isComponentValid || (wizardData.value.baseData.priorLossHistoryRecords.length > 0 && !_.isUndefined(wizardData.value.baseData.agmtSubTypInd_Ext) && !wizardData.value.baseData.agmtSubTypInd_Ext)
        } 
        
        return !isComponentValid
        
    }

    return (
        <WizardPage onNext={onNext} onSave={onSave} disableNext={isDisableNext()} skipWhen={initialValidation}>
            <ViewModelForm
                uiProps={metadata}
                model={wizardData}
                overrideProps={overrideProps}
                onModelChange={onModelChange}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
}

RiskAnalysisPage.propTypes = wizardProps;
export default RiskAnalysisPage;
